<script>
import goFetch from '@/utils/goFetch'

export default {
  name: 'ProvideApiFetch',
  props: {
    endpoint: {
      type: String,
      default: '',
    },
    unpackFunction: {
      type: Function,
      default: null,
    },
    keyName: {
      type: String,
      default: '',
    },
    /**
     * Array of objects where each object must only have 3 parameters of these types.
     * The keyName and endpoint are required to be provided while the unpackFunction is optional
     * {
     *   keyName: key of the dataset
     *   endpoint: endpoint to fetch data from
     *   unpackFunction: Function that unpacks the result of the fetch, if no function is given it will
     *                   assume the fetch returned is in the desired format
     * }
     */
    endpoints: {
      type: Array,
      default() {
        return []
      },
      validator(arrOpts) {
        if (arrOpts !== undefined && !(arrOpts instanceof Array)) return false
        return arrOpts.every((opts) => {
          const requiredProps = Object.keys(opts).every((key) =>
            ['keyName', 'endpoint', 'unpackFunction'].includes(key),
          )
          return (
            typeof opts.endpoint === 'string' &&
            typeof opts.keyName === 'string' &&
            (opts.unpackFunction === undefined || opts.unpackFunction instanceof Function) &&
            requiredProps
          )
        })
      },
    },
    /**
     * Weather to skip fetching the data initially.
     * If true flip it to false when you want the component to fetch the data
     */
    skip: {
      type: Boolean,
      default: false,
    },
    fetchHtml: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      optionSet: {},
      loadingQueue: 0,
    }
  },
  watch: {
    skip() {
      this.fetchAway()
    },
  },
  created() {
    this.fetchAway()
  },
  methods: {
    fetchAway() {
      if (this.skip) return // We don't fetch if we have been marked as skipped
      if (this.endpoints.length !== 0) {
        for (const option of this.endpoints) {
          this.goFetch(option)
        }
      } else {
        this.goFetch(this)
      }
    },
    /**
     * @description
     *    Fetches the option given in the endpoints param
     * @param option
     *    The option needed to be fetched
     */
    async goFetch({ keyName, endpoint, unpackFunction, fetchHtml } = { fetchHtml: false }) {
      this.optionSet[keyName] = []
      this.loadingQueue += 1
      const json = await goFetch(endpoint, fetchHtml)
      this.loadingQueue -= 1
      this.optionSet[keyName] = typeof unpackFunction === 'function' ? unpackFunction(json) : json
    },
    /**
     * @description
     *  Generates the results data object used in the rendering
     *  It contains all values added in the original endpoints but have populated the data field on all of them
     * @returns {...option, data: * }[]
     */
    results() {
      if (this.endpoints.length > 0) {
        return this.endpoints.map((opt) => ({
          ...opt,
          data: this.optionSet[opt.keyName] ?? [],
        }))
      }
      return [
        {
          ...this.$props,
          data: this.optionSet[this.keyName] ?? [],
        },
      ]
    },
  },
  render() {
    return this.$slots.default({
      results: this.results(),
      loading: this.loadingQueue !== 0,
    })
  },
}
</script>
